import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  TextInput,
  PasswordInput,
  Button,
  Container,
  Alert,
  Anchor,
  Title,
  Stack,
  Image,
  Box,
  Paper,
  Text,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { useAuthStore } from '../../../application/store/authStore';
import { ResetPasswordCommand } from '../../../application/dto/auth/ResetPasswordCommand';
import { PUBLIC_ROUTES } from '../../routing/routes';
import LogoSvg from '../../../assets/images/logo.svg';

interface ResetPasswordFormData {
  email: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * ResetPasswordPage — MineMiner SPA password-reset completion screen.
 *
 * sprint-107 S1.5 (epic-013 Sprint D — Stream A item A2; brand-architecture
 * hotfix). Rep-facing surface — branding matches LoginPage (MineMiner logo,
 * "your MineMiner account" copy). Per memory
 * `project_mineminer_brand_architecture`: mymineminer.com is our
 * customer-facing SaaS; never Sync-branded mid-flow for reps.
 *
 * Reads `?uid=` + `?token=` from the URL query string (matches the link
 * the MineMinerForgotPasswordCommandHandler builds against
 * MineMiner:RepBaseUrl = https://mymineminer.com), validates the new
 * password against MineMiner Identity's stricter policy (12 chars + upper
 * + lower + digit + non-alphanumeric, per
 * MineMinerIdentityServiceExtensions.cs:36-41), then POSTs
 * `/api/mineminer/auth/reset-password`. On success redirects to `/login`
 * after a short pause.
 *
 * sprint-281 (S001/D2): the link now carries `?uid=` instead of `?email=`, so
 * no Email field is shown. A link sent before that release still carries
 * `?email=` and gets the old pre-filled Email field.
 */
export const ResetPasswordPage: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetPassword = useAuthStore((state) => state.resetPassword);
  const passwordResetState = useAuthStore((state) => state.passwordResetState);
  const passwordResetError = useAuthStore((state) => state.passwordResetError);

  const [tokenError, setTokenError] = useState<string | null>(null);

  const token = searchParams.get('token') ?? '';
  const userId = searchParams.get('uid') ?? '';
  const emailFromUrl = searchParams.get('email') ?? '';

  const form = useForm<ResetPasswordFormData>({
    // sprint-107 SESSION_004 Hotfix #6: surface validation errors inline as the
    // user types so they can see *which* requirement isn't met. Previously this
    // form gated the submit button on `!form.isValid()` with no inline error
    // display, which produced a "button is just disabled, why?" user experience
    // — the user couldn't tell whether they'd missed the symbol, a digit, the
    // length floor, or that confirmPassword !== newPassword. With this on, each
    // field shows its own error inline as soon as it's touched.
    validateInputOnChange: true,
    initialValues: {
      email: emailFromUrl,
      newPassword: '',
      confirmPassword: '',
    },
    validate: {
      email: (value: string): string | null => {
        if (userId) {
          return null;
        }
        if (!value || value.trim().length === 0) {
          return 'Email is required';
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return 'Invalid email format';
        }
        return null;
      },
      // Mirrors MineMinerIdentityServiceExtensions password policy so the user
      // gets immediate feedback instead of a backend rejection.
      newPassword: (value: string): string | null => {
        if (!value || value.trim().length === 0) {
          return 'Password is required';
        }
        if (value.length < 12) {
          return 'Password must be at least 12 characters';
        }
        if (!/[A-Z]/.test(value)) {
          return 'Password must contain at least one uppercase letter';
        }
        if (!/[a-z]/.test(value)) {
          return 'Password must contain at least one lowercase letter';
        }
        if (!/[0-9]/.test(value)) {
          return 'Password must contain at least one number';
        }
        if (!/[^A-Za-z0-9]/.test(value)) {
          return 'Password must contain at least one non-alphanumeric character';
        }
        return null;
      },
      confirmPassword: (value: string, values: ResetPasswordFormData): string | null => {
        if (value !== values.newPassword) {
          return 'Passwords do not match';
        }
        return null;
      },
    },
  });

  useEffect(() => {
    if (emailFromUrl) {
      form.setFieldValue('email', emailFromUrl);
    }
    // form is stable across renders; intentionally omit from deps to avoid
    // re-running on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailFromUrl]);

  useEffect(() => {
    if (!token) {
      setTokenError('Invalid or missing reset token. Please request a new password reset.');
    } else {
      setTokenError(null);
    }
  }, [token]);

  useEffect(() => {
    if (passwordResetState === 'resetSuccess') {
      const timer = setTimeout(() => {
        navigate(PUBLIC_ROUTES.LOGIN);
      }, 3000);
      return (): void => clearTimeout(timer);
    }
    return undefined;
  }, [passwordResetState, navigate]);

  const handleSubmit = async (values: ResetPasswordFormData): Promise<void> => {
    if (!token) {
      return;
    }

    const command: ResetPasswordCommand = {
      ...(userId ? { userId } : { email: values.email }),
      token,
      newPassword: values.newPassword,
    };

    try {
      await resetPassword(command);
      form.reset();
    } catch {
      // Error state is captured in the auth store; the Alert below renders it.
    }
  };

  const showError = tokenError !== null
    || (passwordResetState === 'error' && passwordResetError !== null);
  const errorMessage = tokenError ?? passwordResetError;

  return (
    <Box mih="100vh" bg="gray.0" py={80} px="md">
      <Container size="xs">
        <Paper bg="white" p={48} radius="md" shadow="sm" withBorder>
          <Stack gap="md" align="center" mb="md">
            <Image src={LogoSvg} alt="MineMiner" h={48} w="auto" />
            <Title order={1} size="h2" ta="center">
              Reset your password
            </Title>
            <Text size="sm" c="dimmed" ta="center">
              Choose a new password for your MineMiner account.
            </Text>
          </Stack>

          {showError && errorMessage !== null && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              variant="light"
              mb="md"
              title="Error"
            >
              {errorMessage}
            </Alert>
          )}

          {passwordResetState === 'resetSuccess' && (
            <Alert
              icon={<IconCheck size={16} />}
              color="green"
              variant="light"
              mb="md"
              title="Password reset successful"
            >
              Your password has been reset. Redirecting to the sign-in page&hellip;
            </Alert>
          )}

          {passwordResetState !== 'resetSuccess' && tokenError === null && (
            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack gap="md">
                {!userId && (
                  <TextInput
                    label="Email"
                    type="email"
                    placeholder="your@email.com"
                    required
                    size="md"
                    {...form.getInputProps('email')}
                  />
                )}

                <PasswordInput
                  label="New password"
                  placeholder="Your new password"
                  required
                  size="md"
                  description="At least 12 characters with upper, lower, number, and a symbol."
                  {...form.getInputProps('newPassword')}
                />

                <PasswordInput
                  label="Confirm new password"
                  placeholder="Re-enter your new password"
                  required
                  size="md"
                  {...form.getInputProps('confirmPassword')}
                />

                <Button
                  type="submit"
                  size="lg"
                  variant="filled"
                  color="brand"
                  loading={passwordResetState === 'resetting'}
                  fullWidth
                  mt="sm"
                >
                  Reset password
                </Button>
              </Stack>
            </form>
          )}

          <Text ta="center" size="sm" mt="md">
            <Anchor component={Link} to={PUBLIC_ROUTES.LOGIN} fw={600}>
              Back to sign in
            </Anchor>
          </Text>
        </Paper>
      </Container>
    </Box>
  );
};
